import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { TrainingContentProgressDto } from '../../../api/types/dtos/training';
import { useUploadOrganizationFile, useOrganizationFilesByEntity } from '../../../api/hooks/useOrganizationFiles';
import { useTrainingProgressById } from '../../../api/hooks/useTrainingProgress';

interface ProgressEditModalProps {
  open: boolean;
  onClose: () => void;
  progress: TrainingContentProgressDto | null;
  organizationName: string;
  contentName: string;
  organizationId: string;
  enrollmentId: string;
  onSave: (updated: Partial<TrainingContentProgressDto>) => Promise<void>;
}

const ProgressEditModal: React.FC<ProgressEditModalProps> = ({
  open,
  onClose,
  progress,
  organizationName,
  contentName,
  organizationId,
  enrollmentId,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<TrainingContentProgressDto>>({});
  const [completedAtDate, setCompletedAtDate] = useState<Date | null>(null);
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [useFileUpload, setUseFileUpload] = useState(false);
  // Track uploaded files: Map of file name -> { uploaded: boolean }
  const [uploadedFiles, setUploadedFiles] = useState<Map<string, boolean>>(new Map());
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set()); // Track files currently being uploaded
  const { enqueueSnackbar } = useSnackbar();
  const uploadFileMutation = useUploadOrganizationFile(organizationId);

  // Fetch full progress object when modal opens and we have a progress ID
  const shouldFetchProgress = Boolean(open && progress?.id && progress.id.trim() !== '');
  const { data: fullProgress, isLoading: isLoadingProgress, error: progressError } = useTrainingProgressById(
    organizationId,
    enrollmentId,
    progress?.id || '',
    { enabled: shouldFetchProgress }
  );

  // Fetch evidence files if progress exists
  const shouldFetchFiles = Boolean(open && progress?.id && progress.id.trim() !== '');
  const { data: evidenceFilesData, isLoading: isLoadingFiles } = useOrganizationFilesByEntity(
    organizationId,
    'TRAINING_PROGRESS',
    progress?.id || '',
    undefined,
    { enabled: shouldFetchFiles }
  );

  // Update form data when full progress is loaded
  useEffect(() => {
    if (fullProgress && open) {
      const progressData = fullProgress as TrainingContentProgressDto;
      setFormData({
        id: progressData.id,
        enrollmentId: progressData.enrollmentId,
        contentId: progressData.contentId,
        isCompleted: progressData.isCompleted,
        score: progressData.score,
        evidences: progressData.evidences,
        completedAt: progressData.completedAt,
        isActive: progressData.isActive,
      });

      // Parse completedAt date
      if (progressData.completedAt) {
        setCompletedAtDate(new Date(progressData.completedAt));
      } else {
        setCompletedAtDate(null);
      }
    } else if (progress && open && !shouldFetchProgress) {
      // Fallback to partial progress if we can't fetch full progress
      setFormData({
        id: progress.id,
        enrollmentId: progress.enrollmentId,
        contentId: progress.contentId,
        isCompleted: progress.isCompleted,
        score: progress.score,
        evidences: progress.evidences,
        completedAt: progress.completedAt,
        isActive: progress.isActive,
      });

      // Parse completedAt date
      if (progress.completedAt) {
        setCompletedAtDate(new Date(progress.completedAt));
      } else {
        setCompletedAtDate(null);
      }
    } else if (!open) {
      setFormData({});
      setCompletedAtDate(null);
    }
  }, [fullProgress, progress, open, shouldFetchProgress]);

  // Update evidence files when they are loaded
  useEffect(() => {
    if (evidenceFilesData && open) {
      // Note: We can't directly set File objects from the API response
      // The files are already uploaded, so we just mark them as uploaded
      // Users can see them in the list but can't re-upload them
      const existingFileNames = new Set(evidenceFilesData.map(f => f.fileName || ''));
      setUploadedFiles((prev) => {
        const newMap = new Map(prev);
        existingFileNames.forEach(name => {
          if (name) {
            newMap.set(name, true);
          }
        });
        return newMap;
      });
    }
  }, [evidenceFilesData, open]);

  // Reset evidence fields when modal opens/closes
  useEffect(() => {
    if (open) {
      // Don't reset if we're loading files
      if (!shouldFetchFiles) {
        setEvidenceFiles([]);
        setEvidenceUrl('');
        setUseFileUpload(false);
        if (!shouldFetchProgress) {
          setUploadedFiles(new Map());
          setUploadingFiles(new Set());
        }
      }
    } else {
      setEvidenceFiles([]);
      setEvidenceUrl('');
      setUseFileUpload(false);
      setUploadedFiles(new Map());
      setUploadingFiles(new Set());
    }
  }, [open, shouldFetchFiles, shouldFetchProgress]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const newFiles = Array.from(fileList);
      setEvidenceFiles((prev) => {
        // Add new files, avoiding duplicates by name
        const existingNames = new Set(prev.map(f => f.name));
        const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));

        if (uniqueNewFiles.length < newFiles.length) {
          enqueueSnackbar(
            `${newFiles.length - uniqueNewFiles.length} archivo(s) ya estaban seleccionados y fueron omitidos`,
            { variant: 'info' }
          );
        }

        return [...prev, ...uniqueNewFiles];
      });
    }
    // Reset the input so the same file can be selected again if needed
    event.target.value = '';
  };

  const handleRemoveFile = (fileName: string) => {
    setEvidenceFiles((prev) => prev.filter(f => f.name !== fileName));
    setUploadedFiles((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileName);
      return newMap;
    });
    setUploadingFiles((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fileName);
      return newSet;
    });
  };

  const handleSave = async () => {
    try {
      let filesUploadedCount = 0;

      // If evidences is enabled, handle file/URL upload
      if (formData.evidences && progress?.id) {
        if (useFileUpload && evidenceFiles.length > 0) {
          // Upload files that haven't been uploaded yet
          const filesToUpload = evidenceFiles.filter(file => {
            return !uploadedFiles.get(file.name);
          });

          if (filesToUpload.length > 0) {
            // Upload files sequentially to avoid overwhelming the server
            for (const file of filesToUpload) {
              try {
                setUploadingFiles((prev) => new Set(prev).add(file.name));

                await uploadFileMutation.mutateAsync({
                  file,
                  entityId: progress.id,
                  entityType: 'TRAINING_PROGRESS',
                  description: `Evidence for ${contentName}: ${file.name}`,
                });

                // Mark as uploaded
                setUploadedFiles((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(file.name, true);
                  return newMap;
                });
                filesUploadedCount++;
              } catch (error) {
                enqueueSnackbar(`Error uploading file ${file.name}`, { variant: 'error' });
                console.error(`Error uploading file ${file.name}:`, error);
                setUploadingFiles((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(file.name);
                  return newSet;
                });
                return; // Don't proceed with save if any upload fails
              } finally {
                setUploadingFiles((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(file.name);
                  return newSet;
                });
              }
            }
            // Don't show success message here - wait until PUT succeeds
          }
        } else if (!useFileUpload && evidenceUrl.trim()) {
          // For URL, we could store it in a description or create a file record with URL
          // For now, we'll just proceed with the save
          // You might want to create an OrganizationFile with the URL if your API supports it
        } else if (formData.evidences && evidenceFiles.length === 0 && !evidenceUrl.trim()) {
          enqueueSnackbar('Please provide either file(s) or URL for evidence', { variant: 'warning' });
          return;
        }
      }

      const updates: Partial<TrainingContentProgressDto> = {
        ...formData,
      };

      // If completed, ensure completedAt is set
      if (formData.isCompleted && !completedAtDate && !formData.completedAt) {
        updates.completedAt = new Date().toISOString();
      } else if (completedAtDate) {
        updates.completedAt = completedAtDate.toISOString();
      }

      // If not completed, clear completedAt
      if (!formData.isCompleted) {
        updates.completedAt = undefined;
      }

      // Wait for the PUT request to complete
      await onSave(updates);

      // Only show success and close modal if PUT succeeds
      if (filesUploadedCount > 0) {
        enqueueSnackbar(`${filesUploadedCount} archivo(s) de evidencia y progreso actualizados exitosamente`, { variant: 'success' });
      } else {
        enqueueSnackbar('Progreso actualizado exitosamente', { variant: 'success' });
      }

      onClose();
    } catch (error) {
      // If PUT fails, show error and keep modal open
      enqueueSnackbar('Error al actualizar el progreso. Por favor, intente nuevamente.', { variant: 'error' });
      console.error('Error saving progress:', error);
      // Modal stays open so user can retry
    }
  };

  const handleChange = (field: keyof TrainingContentProgressDto, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCompletedChange = (isCompleted: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isCompleted,
    }));

    // Auto-set completedAt when marking as complete
    if (isCompleted && !completedAtDate) {
      setCompletedAtDate(new Date());
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Training Progress</DialogTitle>
      <DialogContent>
        {isLoadingProgress && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading progress data...
            </Typography>
          </Box>
        )}

        {progressError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading progress data. Using partial information.
          </Alert>
        )}

        {!isLoadingProgress && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Organization
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {organizationName}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Content
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {contentName}
              </Typography>
            </Box>

            {/* Completion Status */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.isCompleted ? 'completed' : 'incomplete'}
                onChange={(e) => handleCompletedChange(e.target.value === 'completed')}
                label="Status"
              >
                <MenuItem value="incomplete">Incomplete</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>

            {/* Score */}
            <TextField
              label="Score"
              type="number"
              value={formData.score || ''}
              onChange={(e) =>
                handleChange('score', e.target.value ? Number(e.target.value) : undefined)
              }
              fullWidth
              inputProps={{ min: 0, max: 100 }}
              helperText="Enter a score between 0 and 100"
            />

            {/* Evidences */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.evidences || false}
                  onChange={(e) => handleChange('evidences', e.target.checked)}
                />
              }
              label="Has Evidences"
            />

            {/* Evidence Upload Section */}
            {formData.evidences && progress?.id && (
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Evidence Upload
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={useFileUpload}
                      onChange={(e) => {
                        setUseFileUpload(e.target.checked);
                        if (!e.target.checked) {
                          // Reset file upload state when switching back to URL
                          setEvidenceFiles([]);
                          setUploadedFiles(new Map());
                          setUploadingFiles(new Set());
                        }
                      }}
                    />
                  }
                  label="Upload File(s)"
                  sx={{ mb: 2 }}
                />

                {useFileUpload ? (
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUpload />}
                      fullWidth
                      disabled={uploadFileMutation.isPending || uploadingFiles.size > 0}
                    >
                      {evidenceFiles.length > 0
                        ? `Add More Files (${evidenceFiles.length} selected)`
                        : 'Select Files (Multiple)'}
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                        accept="*/*"
                        disabled={uploadFileMutation.isPending || uploadingFiles.size > 0}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      You can select multiple files at once (Ctrl+Click or Cmd+Click)
                    </Typography>

                    {/* Display existing files from API */}
                    {isLoadingFiles && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Loading existing files...</Typography>
                      </Box>
                    )}

                    {evidenceFilesData && evidenceFilesData.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Existing files ({evidenceFilesData.length}):
                        </Typography>
                        {evidenceFilesData.map((file) => (
                          <Box
                            key={file.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor: '#f9f9f9',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                flex: 1,
                                color: 'success.main',
                              }}
                            >
                              {file.fileName} ✓
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {evidenceFiles.length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          New files to upload ({evidenceFiles.length}):
                        </Typography>
                        {evidenceFiles.map((file) => {
                          const isUploaded = uploadedFiles.get(file.name) || false;
                          const isUploading = uploadingFiles.has(file.name);

                          return (
                            <Box
                              key={file.name}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                {isUploading && <CircularProgress size={16} />}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flex: 1,
                                    color: isUploaded ? 'success.main' : 'text.primary',
                                  }}
                                >
                                  {file.name}
                                  {isUploaded && ' ✓'}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(file.name)}
                                disabled={isUploading}
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {uploadingFiles.size > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">
                          Uploading {uploadingFiles.size} file(s)...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <TextField
                    label="Evidence URL"
                    value={evidenceUrl}
                    onChange={(e) => setEvidenceUrl(e.target.value)}
                    fullWidth
                    placeholder="https://..."
                    helperText="Enter a URL to the evidence"
                  />
                )}
              </Box>
            )}

            {/* Completed At */}
            {formData.isCompleted && (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DateTimePicker
                  label="Completed At"
                  value={completedAtDate}
                  onChange={(newValue) => setCompletedAtDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Date and time when the content was completed',
                      placeholder: '',
                    },
                  }}
                />
              </LocalizationProvider>
            )}

            {/* Active Status */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive !== false}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={uploadFileMutation.isPending || uploadingFiles.size > 0}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={uploadFileMutation.isPending || uploadingFiles.size > 0}
          startIcon={(uploadFileMutation.isPending || uploadingFiles.size > 0) && <CircularProgress size={16} />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgressEditModal;

